import type { FC } from "react";

import { InputField } from "@/ui/components/form/Input/InputField";
import { Column, Container } from "@/ui/components/layout";
import { Button } from "@/ui/components/primitives/Button";
import { TextSubtitle } from "@/ui/components/typography/TextSubtitle";
import { cn } from "@/ui/lib";
import { useTranslations } from "next-intl";

import {
  NEWSLETTER_SIGNUP_DEFAULT_INPUT,
  NEWSLETTER_SIGNUP_DEFAULTS,
  type TNewsletterSignupProps,
} from "./NewsletterSignup.config";

export const NewsletterSignup: FC<TNewsletterSignupProps> = (
  {
    "data-component": component = NEWSLETTER_SIGNUP_DEFAULTS["data-component"],
    title,
    body,
    button,
    input = NEWSLETTER_SIGNUP_DEFAULT_INPUT,
    className,
    ...props
  },
) => {
  const t = useTranslations("Components.Marketing.NewsletterSignUp");

  const resolvedButton = {
    ...NEWSLETTER_SIGNUP_DEFAULTS.button,
    ...button,
    children: button?.children ?? t("button.children"),
  };

  const inputPlaceholder = input.placeholder ?? t("inputs.email.placeholder");

  return (
    <form
      {...props}
      data-component={component}
      className={cn("w-full bg-inverse", className)}
    >
      <Container className="flex flex-col items-start">
        <div
          data-component="NewsletterSignupControls"
          className="flex w-full flex-col gap-4 lg:flex-row"
        >
          <Column className="min-w-0 flex-1 flex-col items-start gap-4">
            <Column className="w-full flex-col items-start gap-1">
              <TextSubtitle
                tag="span"
                size={3}
                isResponsive={false}
                className="!text-sys-chartreuse-600"
              >
                { title ?? t("title") }
              </TextSubtitle>
              <TextSubtitle
                tag="h2"
                size={2}
                isResponsive={false}
                className="!text-sys-white"
              >
                { body ?? t("body") }
              </TextSubtitle>
            </Column>
            <InputField
              {...input}
              placeholder={inputPlaceholder}
              className={cn("min-h-9 border-strong", input.className)}
            />
          </Column>
          <Column className="shrink-0 self-start lg:self-end">
            <Button type="submit" variant="secondary" size="lg" state={resolvedButton.state}>
              { resolvedButton.children }
            </Button>
          </Column>
        </div>
      </Container>
    </form>
  );
};

NewsletterSignup.displayName = "NewsletterSignup";
